import { forwardRef, useImperativeHandle, useState, type Ref } from "react";
import dayjs from "dayjs";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { toast } from "../../../utils/snackbarHelper";
import { useRouteAssignment } from "../../../hooks/react-query/useRouteAssignment";
import type { EditedRoute } from "../../../hooks/react-query/useDeliveryRoutes.types";
import { planningStatus } from "../../../types/status.type";
import { toastMessage } from "../../../constants/strings";
import type {
  RouteAssignmentResponse,
  TrapAssignmentResponse,
} from "../../../types/planning/routeAssignmentResponse.type";
import type { PlanTableFormat } from "../../../types/planning/plan.type";
import { actionsMap, type ShipmentActionTypeId } from "./ShipmentActionDialog";
import {
  EditRouteConfirmDialog,
  type EditedPlan,
} from "./EditRouteConfirmDialog";

type EditRouteOrTrapDialog = {
  routeOrTrap: PlanTableFormat;
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: (
    actionType: ShipmentActionTypeId,
    response?: RouteAssignmentResponse | TrapAssignmentResponse
  ) => void;
  clearSelection?: () => void;
};

type EquipmentRestrictionParams = {
  type: "delivery_trap" | "delivery_route";
  status: string;
  doorId?: number | null;
  trailerId?: number | null;
  straightTruckId?: number | null;
};

const isEquipmentRestricted = ({
  type,
  status,
  doorId,
  trailerId,
  straightTruckId,
}: EquipmentRestrictionParams): {
  restricted: boolean;
  missingEquipment?: string;
} => {
  const isRestrictedStatus = ["CLDK", "CLDV", "DISP", "TRAP"].includes(status);

  const equipment =
    type === "delivery_trap"
      ? [
          { key: "Door", value: !doorId },
          {
            key: "Trailer / Straight Truck",
            value: !trailerId && !straightTruckId,
          },
        ]
      : [{ key: "Door", value: !doorId }];

  const missingEquipment = equipment.find((equip) => equip.value)?.key;

  if (isRestrictedStatus && missingEquipment) {
    return { restricted: true, missingEquipment };
  }

  return { restricted: false };
};

type EditRouteOrTrapDialogRef = {
  handleOpen: () => void;
};

const EditRouteOrTrapDialog = forwardRef(
  (
    {
      routeOrTrap,
      isOpen = false,
      onClose,
      onSuccess,
      clearSelection,
    }: EditRouteOrTrapDialog,
    ref?: Ref<EditRouteOrTrapDialogRef>
  ) => {
    const [isEditPlanOpen, setIsEditPlanOpen] = useState(isOpen);

    const [serviceCenter] = useSelectedServiceCenter();
    const [selectedDate] = useDateSearchParamOrFallbackToToday();
    const planDate = dateToInt(selectedDate);

    useImperativeHandle(ref, () => ({
      handleOpen: () => {
        if (routeOrTrap.status in planningStatus) {
          setIsEditPlanOpen(true);
        } else {
          toast(toastMessage.inbound.editNotAllowedStatus, {
            variant: "warning",
          });
        }
      },
    }));

    const defaultOnSuccessfulAssignment = (
      actionType: ShipmentActionTypeId
    ) => {
      const { description } = actionsMap[actionType];

      toast(description, {
        variant: "success",
      });
    };

    const { mutate: editRouteOrTrap, isPending } = useRouteAssignment({
      date: planDate,
      sicId: serviceCenter.id,
      type: routeOrTrap.type,
      routeOrTrapId: routeOrTrap.id,
      searchTerm: "",
      onSuccess: (response, request) =>
        onSuccess
          ? onSuccess(
              request?.route.type === "delivery_route"
                ? "editRoute"
                : "editTrap",
              response
            )
          : defaultOnSuccessfulAssignment(
              request?.route.type === "delivery_route"
                ? "editRoute"
                : "editTrap"
            ),
      onSettled: () => {
        onClose?.();
        clearSelection?.();
      },
    });

    const handleEditRoute = (route: EditedRoute) => {
      editRouteOrTrap({
        planId: route.id,
        route,
      });
    };

    const editHandler = (editedPlan: EditedPlan) => {
      const editedRoute: EditedRoute = {
        id: editedPlan.id,
        date: routeOrTrap.date,
        sicId: routeOrTrap.sicId,
        name: editedPlan.name.trim(),
        presetRouteId: routeOrTrap.presetRouteId,
        driverId: editedPlan.driverId,
        isCommitted: Boolean(routeOrTrap.isCommitted),
        endPoint: editedPlan.routeEnd ?? "",
        type: routeOrTrap.type,
        trailerEquipmentId: editedPlan.trailerEquipmentId,
        tractorEquipmentId: editedPlan.tractorEquipmentId,
        straightTruckEquipmentId: editedPlan.straightTruckEquipmentId,
      };

      if (editedPlan.startTime) {
        editedRoute.startTime = dayjs(editedPlan.startTime, {
          utc: false,
        }).toISOString();
      }

      if (editedPlan.endTime) {
        editedRoute.endTime = dayjs(editedPlan.endTime).utc().toISOString();
      }
      if (editedPlan.doorId) {
        editedRoute.doorId = editedPlan.doorId;
      }

      const { restricted, missingEquipment } = isEquipmentRestricted({
        type: routeOrTrap.type,
        status: routeOrTrap.status,
        doorId: editedRoute.doorId,
        trailerId: editedRoute.trailerEquipmentId,
        straightTruckId: editedRoute.straightTruckEquipmentId,
      });

      if (restricted && missingEquipment) {
        const errorMessage =
          toastMessage.inbound.removeEquipmentNotAllowedFromTrapOrRoute(
            missingEquipment,
            routeOrTrap.type === "delivery_trap" ? "Trap" : "Route"
          );
        toast(errorMessage, { variant: "error" });
      }

      handleEditRoute(editedRoute);
    };

    const cancelHandler = () => {
      setIsEditPlanOpen(false);
      onClose?.();
    };

    return (
      <EditRouteConfirmDialog
        plan={routeOrTrap}
        open={isEditPlanOpen}
        isLoading={isPending}
        savePlan={editHandler}
        cancelHandler={cancelHandler}
      />
    );
  }
);

EditRouteOrTrapDialog.displayName = "EditRouteOrTrapDialog";

export default EditRouteOrTrapDialog;
